import { motion } from "framer-motion";
import { AlertTriangle, CheckCircle2, Phone, User } from "lucide-react";
import { Badge } from "../../../components/ui/badge";
import { Card, CardContent } from "../../../components/ui/card";
import { cn } from "../../../lib/utils";
import {
  formatCurrency,
  formatDate,
} from "../../entrego/pages/saldo-consulta/utils/format";
import { CadastroResult } from "../types";
import { StageStepper } from "./stage-stepper";

export function CadastroResultCard({
  cadastro,
  index,
}: {
  cadastro: CadastroResult;
  index: number;
}) {
  const ativo = cadastro.cadastroAtivo;
  const notSent = cadastro.lastStage === "nao_enviada";

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.05 }}
    >
      <Card
        className={cn(
          "border-2 transition-colors",
          ativo ? "border-primary/40" : "border-muted opacity-90"
        )}
      >
        <CardContent className="space-y-4 pt-6">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <User className="h-4 w-4 text-muted-foreground" />
              <span className="font-semibold">
                {cadastro.nome || "Sem nome"}
              </span>
              {ativo ? (
                <Badge className="bg-emerald-100 text-emerald-700">
                  Cadastro ativo
                </Badge>
              ) : (
                <Badge variant="outline" className="text-muted-foreground">
                  Cadastro inativo
                </Badge>
              )}
            </div>
            <Badge variant="outline">{cadastro.ops}</Badge>
          </div>

          <div className="grid grid-cols-1 gap-2 text-sm md:grid-cols-3">
            <div>
              <span className="text-muted-foreground">Documento: </span>
              <span className="font-mono">{cadastro.numeroDocumento}</span>
            </div>
            <div className="flex items-center gap-1">
              <Phone className="h-3.5 w-3.5 text-muted-foreground" />
              <span className="font-mono">{cadastro.telefone}</span>
            </div>
            <div>
              <span className="text-muted-foreground">Status: </span>
              {cadastro.status}
            </div>
          </div>

          <div
            className={cn(
              "flex items-start gap-2 rounded-md border p-3 text-sm",
              notSent
                ? "border-destructive/40 bg-destructive/5 text-destructive"
                : "border-emerald-500/30 bg-emerald-50 text-emerald-800"
            )}
          >
            {notSent ? (
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            ) : (
              <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" />
            )}
            <span>
              {notSent
                ? "Parou em: Não enviada — não há registro de envio para este cadastro."
                : cadastro.mensagemEnviadaTexto}
              {!ativo && !notSent && (
                <strong className="ml-1">
                  (atenção: enviada para um cadastro INATIVO)
                </strong>
              )}
            </span>
          </div>

          <StageStepper lastStage={cadastro.lastStage} startFromEnviada />

          {(cadastro.solicitacoes?.length ?? 0) > 0 ? (
            <div className="space-y-2">
              <p className="text-xs font-semibold uppercase text-muted-foreground">
                Ofertas no período ({cadastro.solicitacoes.length})
              </p>
              <div className="overflow-x-auto rounded-md border">
                <table className="w-full text-sm">
                  <thead className="bg-muted/60 text-left">
                    <tr>
                      <th className="px-3 py-2 font-medium">Romaneio</th>
                      <th className="px-3 py-2 font-medium">Vencimento</th>
                      <th className="px-3 py-2 font-medium">Valor face</th>
                      <th className="px-3 py-2 font-medium">Valor compra</th>
                      <th className="px-3 py-2 font-medium">Status</th>
                      <th className="px-3 py-2 font-medium">Comprovante</th>
                      <th className="px-3 py-2 font-medium">Sol ID</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {cadastro.solicitacoes.map((sol) => (
                      <tr key={sol.solId} className="bg-muted/20">
                        <td className="px-3 py-2 whitespace-nowrap">{formatDate(sol.dataRomaneio ?? "")}</td>
                        <td className="px-3 py-2 whitespace-nowrap">{formatDate(sol.dataVencimento ?? "")}</td>
                        <td className="px-3 py-2 whitespace-nowrap">{formatCurrency(sol.valorFace)}</td>
                        <td className="px-3 py-2 whitespace-nowrap">{formatCurrency(sol.valorCompra)}</td>
                        <td className="px-3 py-2">
                          <div className="flex flex-wrap gap-1">
                            {sol.baixado ? (
                              <Badge className="bg-emerald-100 text-emerald-700">Baixado</Badge>
                            ) : sol.comprado ? (
                              <Badge className="bg-blue-100 text-blue-700">Comprado</Badge>
                            ) : sol.liquidado ? (
                              <Badge className="bg-violet-100 text-violet-700">Liquidado</Badge>
                            ) : (
                              <Badge variant="outline">Aceite</Badge>
                            )}
                          </div>
                        </td>
                        <td className="px-3 py-2">
                          {sol.comprovanteBancario ? (
                            /^https?:\/\//.test(sol.comprovanteBancario) ? (
                              <a
                                href={sol.comprovanteBancario}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-primary underline"
                              >
                                Ver comprovante
                              </a>
                            ) : (
                              <span className="font-mono text-xs">{sol.comprovanteBancario}</span>
                            )
                          ) : (
                            <span className="text-muted-foreground">—</span>
                          )}
                        </td>
                        <td className="px-3 py-2 font-mono text-xs">{sol.solId}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            cadastro.solicitacao && (
              <div className="grid grid-cols-2 gap-2 rounded-md bg-muted/40 p-3 text-sm md:grid-cols-4">
                <div>
                  <span className="text-muted-foreground">Romaneio: </span>
                  {formatDate(cadastro.solicitacao.dataRomaneio)}
                </div>
                <div>
                  <span className="text-muted-foreground">Vencimento: </span>
                  {formatDate(cadastro.solicitacao.dataVencimento)}
                </div>
                <div>
                  <span className="text-muted-foreground">Valor face: </span>
                  {formatCurrency(cadastro.solicitacao.valorFace)}
                </div>
                <div>
                  <span className="text-muted-foreground">Valor compra: </span>
                  {formatCurrency(cadastro.solicitacao.valorCompra)}
                </div>
              </div>
            )
          )}
        </CardContent>
      </Card>
    </motion.div>
  );
}
